import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { PageToolbar, EmptyState, StatusPill } from "@/components/PageToolbar";
import CrudDialog from "@/components/CrudDialog";
import ImportButton from "@/components/ImportButton";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

export function makeResourcePage({ resource, testid, title, columns, fields, exportName, importable, permissionModule }) {
  return function ResourcePage() {
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === "admin";
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, q);

    const load = async () => {
      const { data } = await api.get(`/${resource}`, { params: q ? { q } : {} });
      setRows(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    const save = async (form) => {
      try {
        if (editing) {
          await api.put(`/${resource}/${editing.id}`, { ...editing, ...form });
          toast.success("Updated");
        } else {
          await api.post(`/${resource}`, form);
          toast.success("Created");
        }
        await load();
      } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    };

    const del = async (r) => {
      if (!window.confirm("Delete this record?")) return;
      try {
        await api.delete(`/${resource}/${r.id}`);
        toast.success("Deleted");
        await load();
      } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    };

    return (
      <div data-testid={testid}>
        <PageToolbar
          onNew={() => { setEditing(null); setOpen(true); }}
          newLabel={`New ${title}`}
          exportName={isAdmin ? (exportName || resource) : null}
          searchValue={q}
          onSearchChange={setQ}
          extra={isAdmin && importable ? (
            <ImportButton resource={resource} onDone={load} label={`Import ${title}s`} />
          ) : undefined}
        />
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm crm-table">
              <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold w-10">Sr.</th>
                  {columns.map((c) => (
                    <th key={c.key} className="px-3 py-2.5 text-left font-semibold">{c.label}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((r, i) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-slate-500 font-mono-num">{(page - 1) * 50 + i + 1}</td>
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${c.mono ? "font-mono-num" : ""} ${c.className || ""}`}>
                        {c.render ? c.render(r) : (c.key === "active" ? <StatusPill active={r.active !== false} /> : (r[c.key] ?? ""))}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button data-testid="edit-btn" onClick={() => { setEditing(r); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded text-[color:var(--brand-teal)]"><Pencil className="h-4 w-4" /></button>
                        <button data-testid="delete-btn" onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <EmptyState />}
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid={`${testid}-pagination`} />
        </div>
        <CrudDialog open={open} setOpen={setOpen} title={editing ? `Edit ${title}` : `New ${title}`} fields={fields} value={editing} onSave={save} />
      </div>
    );
  };
}

export const Agents = makeResourcePage({
  resource: "agents",
  testid: "agents-page",
  title: "Agent",
  importable: true,
  permissionModule: "Agent",
  columns: [
    { key: "name", label: "Name" },
    { key: "mobile", label: "Mobile Number", mono: true },
    { key: "active", label: "Status" },
  ],
  fields: [
    { name: "name", label: "Name", required: true },
    { name: "mobile", label: "Mobile Number", type: "mobile", required: true },
    { name: "active", label: "Status", type: "boolean" },
  ],
});

export const Services = makeResourcePage({
  resource: "services",
  testid: "services-page",
  title: "Service",
  importable: true,
  columns: [
    { key: "name", label: "Service Name" },
    { key: "rate", label: "Fees (₹)", mono: true, render: (r) => r.rate ? Number(r.rate).toLocaleString("en-IN") : "—" },
    { key: "order", label: "Order", mono: true },
    { key: "active", label: "Status" },
  ],
  fields: [
    { name: "name", label: "Service Name", required: true },
    { name: "rate", label: "Fees / Amount (₹)", type: "number" },
    { name: "order", label: "Display Order", type: "number" },
    { name: "description", label: "Service Description", type: "textarea", full: true },
    { name: "documents", label: "Required Documents (one per line)", type: "textarea", full: true },
    { name: "notes", label: "Notes / Instructions", type: "textarea", full: true },
    { name: "active", label: "Status", type: "boolean" },
  ],
});

export const Branches = makeResourcePage({
  resource: "branches",
  testid: "branches-page",
  title: "Branch",
  importable: true,
  columns: [
    { key: "name", label: "Branch Name" },
    { key: "code", label: "Code", mono: true },
    { key: "email", label: "Login Email", mono: true },
    { key: "phone", label: "Phone", mono: true },
    { key: "active", label: "Status" },
  ],
  fields: [
    { name: "name", label: "Branch Name", required: true },
    { name: "code", label: "Code" },
    { name: "email", label: "Login Email", type: "email" },
    { name: "password", label: "Password (leave blank to keep)", type: "password" },
    { name: "phone", label: "Phone", type: "mobile" },
    { name: "working_hours", label: "Working Hours", full: false },
    { name: "address", label: "Address", type: "textarea", full: true },
    { name: "active", label: "Status", type: "boolean" },
  ],
});

export const PaymentTypes = makeResourcePage({
  resource: "payment-types",
  testid: "payment-types-page",
  title: "Payment Type",
  importable: true,
  columns: [
    { key: "payment_type", label: "Payment" },
    { key: "active", label: "Status", render: (v) => (v === false ? "Inactive" : "Active") },
  ],
  fields: [
    { name: "payment_type", label: "Payment", required: true },
    { name: "active", label: "Status", type: "boolean" },
  ],
});
